import { EnterElement, Selection, select } from "d3"
import { D3OneToOneRenderable } from "./D3OneToOneRenderable"
import { ReactCallbacks } from "../../Types/ReactCallbacks"

export type D3NavigationCursorConfig = {
	width: number
	height: number
	xScale?: (value: number) => number
	cursorTime?: number | null
	useCenterPosition: boolean
	visible: boolean
	clipPathId?: string
}

export class D3NavigationCursor extends D3OneToOneRenderable<SVGGElement, SVGGElement, D3NavigationCursorConfig> {
	private lineClassName = "d3-navigation-cursor-line"

	constructor(root: SVGGElement, config: D3NavigationCursorConfig, reactCallbacks: ReactCallbacks<any>) {
		super(root, config, "d3-navigation-cursor", reactCallbacks)
		this.render()
	}

	enter = (newElements: Selection<EnterElement, D3NavigationCursorConfig, any, any>): Selection<any, any, any, any> => {
		const group = newElements.append("g").attr("class", this.className)

		group
			.append("line")
			.attr("class", this.lineClassName)
			.attr("stroke", "rgba(255, 0, 0, 0.75)")
			.attr("stroke-width", 2)
			.attr("y1", 0)
			.attr("y2", this.config.height)
			.attr("pointer-events", "none")

		this.updateClipPath(group)

		return group
	}

	update = (updatedElements: Selection<any, any, any, any>): Selection<any, any, any, any> => {
		this.updateClipPath(updatedElements)
		this.updateLine(updatedElements.select("." + this.lineClassName))

		return updatedElements
	}

	public draw = () => {
		const group = select(this.root).select<SVGGElement>("." + this.className)
		if (group.empty()) {
			return
		}

		this.updateClipPath(group)
		this.updateLine(group.select("." + this.lineClassName))
	}

	public setConfigFast = (partialConfig: Partial<D3NavigationCursorConfig>) => {
		this.config = { ...this.config, ...partialConfig }
		this.draw()
	}

	private updateClipPath = (selection: Selection<any, any, any, any>) => {
		selection.attr("clip-path", this.config.clipPathId ? `url(#${this.config.clipPathId})` : null)
	}

	private updateLine = (lineSelection: Selection<any, any, any, any>) => {
		if (lineSelection.empty() || !this.config.visible) {
			lineSelection.attr("visibility", "hidden")
			return
		}

		let x: number

		if (this.config.useCenterPosition) {
			x = this.config.width / 2
		} else if (this.config.xScale && this.config.cursorTime !== undefined && this.config.cursorTime !== null) {
			x = this.config.xScale(this.config.cursorTime)
		} else {
			lineSelection.attr("visibility", "hidden")
			return
		}

		if (x < 0 || x > this.config.width) {
			lineSelection.attr("visibility", "hidden")
			return
		}

		lineSelection
			.attr("visibility", "visible")
			.attr("x1", x)
			.attr("x2", x)
			.attr("y1", 0)
			.attr("y2", this.config.height)
	}
}
